import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useContext } from 'react';
import { useDownload } from '@europrocurement/l2d-modules/hooks';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { FichiersDossierJsonldDocumentfileRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useDocumentPreviewContext } from '@europrocurement/l2d-modules/modules/Dossiers/providers/DocumentPreviewContext';
import {
    useDossierDocuments,
    useDossierDocumentsFiles,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import { DocumentContext } from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import useFileChecker from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useFileChecker';
import { useSnackbar } from 'notistack';
import { Layout } from '../../../../constants/layouts';
import DossierDocumentsValidationHeader from '../DossierDocumentsValidationHeader/DossierDocumentsValidationHeader';
import DocumentPreviewFooter from '../../DocumentPreviewFooter/DocumentPreviewFooter';
import DocumentViewer from '../../DocumentViewer/DocumentViewer';

const DocumentPreviewOverlayWithActions: React.FunctionComponent = function () {
    const { palette, breakpoints } = useTheme();
    const downMd = useMediaQuery(breakpoints.down('md'));
    const { documentPreview, setDocumentPreview } = useDocumentPreviewContext();
    const { download } = useDownload();
    const { updateFichier } = useDossierDocumentsFiles();
    const { getFichierContent } = useDossierDocuments();
    const { fichiers, setFichiers } = useContext(DocumentContext);
    const { displayFilesCheckerForm } = useFileChecker();
    const { enqueueSnackbar } = useSnackbar();
    // Listener to render document preview
    if (!documentPreview) {
        return null;
    }

    const { fileName, content } = documentPreview;

    const handleClose = () => {
        setDocumentPreview(undefined);
    };

    const handleDownload = () => {
        download(fileName, content);
    };

    // Update fichier context state
    const updateFichierStateContext = (data: FichiersDossierJsonldDocumentfileRead) => {
        setFichiers(
            fichiers.map((fichier) => {
                if (fichier.id === data?.id) {
                    return { ...fichier, ...data };
                }
                return fichier;
            }),
        );
    };

    const handleChangeDocumentType = async (fichierId: number, newTypeDocId: number) => {
        const { success, files } = await updateFichier(fichierId as number, {
            typeDocDossier: newTypeDocId,
        });
        if (success && files?.length === 1) {
            updateFichierStateContext(files[0]);
        }
    };

    const onDocumentStatusChange = async (fichierId: number, statusId: number) => {
        const { success, files } = await updateFichier(fichierId as number, { status: statusId });
        if (success && files?.length === 1) {
            updateFichierStateContext(files[0]);
        }
    };

    const handleChangeLayout = (layoutParam: Layout) => {
        // The url can already have different properties in the query, we just have to update only one property
        const urlParams = new URLSearchParams(document.location.search);
        urlParams.set('layout', layoutParam);
        window.open(
            `${document.location.origin + document.location.pathname}?${urlParams.toString()}`,
            '_blank',
        );
    };

    const handleDeliassage = async (fichierId: number) => {
        const { files } = await updateFichier(fichierId as number, {
            typageAuto: true,
        });

        if (files) {
            const filteredFiles = files.filter((f) => f.id !== fichierId);
            if (filteredFiles.length > 0) {
                // files checker modal
                displayFilesCheckerForm([{ createdFiles: filteredFiles }], getFichierContent);
            } else {
                enqueueSnackbar('Nouceaux documents non détectés', {
                    variant: 'info',
                    autoHideDuration: 2000,
                });
            }
        }
    };

    return (
        <Box // Wrapper
            onClick={handleClose}
            sx={{ width: '100%', height: '100%', position: 'fixed', top: 0, left: 0 }}
        >
            <Box
                onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                sx={{
                    position: 'fixed',
                    top: '0',
                    right: '0',
                    maxWidth: downMd ? '100%' : '50%',
                    width: '100%',
                    height: '100vh',
                    background: palette.background.paper,
                    boxShadow: `1px 1px 15px 0px ${palette.grey[200]}`,
                    borderRadius: '12px',
                    zIndex: 1150,
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: '72px', // minHeight of the user toolbar
                }}
            >
                <DossierDocumentsValidationHeader
                    currentLayout="default"
                    onLayoutChange={handleChangeLayout}
                    onDocumentTypeChange={handleChangeDocumentType}
                    onDocumentStatusChange={onDocumentStatusChange}
                    onDocumentDeliassage={handleDeliassage}
                    navigationBtn={
                        <Button
                            sx={{
                                display: 'flex',
                                gap: '0 8px',
                                alignItems: 'center',
                                color: palette.secondary.main,
                            }}
                            onClick={handleClose}
                        >
                            <FaOptionIcon
                                sx={{
                                    lineHeight: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '14px',
                                }}
                                icon={['fasl', 'close']}
                            />
                            <Typography
                                sx={{ fontSize: '14px' }}
                                variant="caption"
                            >
                                Fermer
                            </Typography>
                        </Button>
                    }
                />
                <Box
                    sx={{
                        margin: '0 30px',
                        border: `solid 1px ${palette.grey[300]}`,
                        boxShadow: `0px 2px 2.5px 0px ${palette.grey[300]}`,
                        height: '80%',
                        borderRadius: '6px',
                        overflow: 'hidden',
                    }}
                >
                    <DocumentViewer />
                </Box>
                <DocumentPreviewFooter onDownload={handleDownload} />
            </Box>
        </Box>
    );
};

export default DocumentPreviewOverlayWithActions;
